import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FalkeTekLogo, FalkeCoinLogo } from "../components/Brand";
import { formatApiErrorDetail } from "../lib/api";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role === "admin" || u.role === "buchhalter") navigate("/admin");
      else if (u.role === "vertriebler") navigate("/vertriebler");
      else navigate("/app");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail)
        || (err.response?.status === 401 ? "E-Mail oder Passwort falsch" : null)
        || (err.response?.status === 403 ? "Account noch nicht aktiviert" : null)
        || (err.response?.status === 429 ? "Zu viele Versuche – bitte 15 Min. warten" : null)
        || (err.response?.status >= 500 ? "Server-Fehler – bitte später erneut versuchen" : null)
        || (!err.response ? "Keine Verbindung zum Server" : null)
        || `Login fehlgeschlagen (Code ${err.response?.status || "?"})`;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-falke-warmwhite flex flex-col">
      <header className="px-6 py-5 bg-white border-b border-falke-blue/10">
        <FalkeTekLogo />
      </header>
      <div className="flex-1 grid lg:grid-cols-[1.1fr_1fr]">
        {/* Form first on mobile */}
        <div className="order-1 lg:order-2 flex items-center justify-center px-4 sm:px-6 py-8 sm:py-10">
          <div className="w-full max-w-md falke-card p-6 sm:p-8" data-testid="login-form">
            <h2 className="text-2xl font-heading font-semibold text-falke-blue mb-2">Willkommen zurück</h2>
            <p className="text-sm text-falke-anthracite/70 mb-6">Melde dich an, um zu deinem FalkeCoin-Konto zu gelangen.</p>

            <form onSubmit={onSubmit} data-testid="login-form-password">
              <label className="block text-sm font-medium text-falke-blue mb-1.5">E-Mail</label>
              <input
                data-testid="login-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-falke-blue/15 focus:outline-none focus:ring-2 focus:ring-falke-orange/40 focus:border-falke-orange mb-4"
                placeholder="max@example.de"
              />
              <label className="block text-sm font-medium text-falke-blue mb-1.5">Passwort</label>
              <div className="relative mb-2">
                <input
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 rounded-xl border border-falke-blue/15 focus:outline-none focus:ring-2 focus:ring-falke-orange/40 focus:border-falke-orange"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-falke-blue/60 hover:text-falke-orange transition p-1"
                  aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="flex justify-end mb-2">
                <Link
                  to="/passwort-vergessen"
                  className="text-xs text-falke-blue/70 hover:text-falke-orange hover:underline"
                  data-testid="link-forgot-password"
                >
                  Passwort vergessen?
                </Link>
              </div>

              {error && <div className="text-sm text-red-600 my-3" data-testid="login-error">{error}</div>}
              <button type="submit" disabled={loading} className="btn-primary w-full mt-4" data-testid="login-submit">
                {loading ? "Anmelden..." : "Anmelden"}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-falke-anthracite/70">
              Noch keinen Account?{" "}
              <Link to="/register" className="text-falke-orange font-semibold hover:underline" data-testid="link-register">
                Jetzt registrieren
              </Link>
            </div>
          </div>
        </div>

        {/* Visual / Marketing */}
        <div className="order-2 lg:order-1 flex flex-col justify-center px-6 sm:px-8 lg:px-12 py-10 sm:py-14 lg:py-16 bg-gradient-to-br from-[#2D5C7E] to-[#1C425E] text-white relative overflow-hidden">
          <div className="absolute -top-20 -right-20 opacity-25 pointer-events-none">
            <FalkeCoinLogo size={300} />
          </div>
          <div className="relative z-10 max-w-md mx-auto lg:mx-0 w-full">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-falke-orange/20 backdrop-blur text-falke-gold text-xs font-semibold uppercase tracking-wider mb-4 sm:mb-6">
              FalkeCoin · 1 FC = 1 €
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-3 sm:mb-4 text-white leading-tight">Empfehlen.<br/>Belohnt werden.</h1>
            <p className="text-white/80 leading-relaxed text-sm sm:text-base">
              Als FalkeTek Bestandskunde sammelst du FalkeCoins für jede erfolgreiche Empfehlung
              und tauschst sie in echte Sachprämien ein. Oder werde gewerblicher Tippgeber mit Geld-Vergütung.
            </p>
            <div className="mt-6 sm:mt-10 space-y-3">
              {[
                { t: "200 FalkeCoins", s: "Standard pro abgeschlossener Empfehlung" },
                { t: "Quartals-Bonus", s: "Mehr Empfehlungen → mehr Coins" },
                { t: "Premium-Geschenke", s: "iPad, E-Bike, Wellness und mehr" },
              ].map((b) => (
                <div key={b.t} className="flex items-start gap-3">
                  <div className="w-2 h-2 mt-2 rounded-full bg-falke-orange flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-sm sm:text-base">{b.t}</div>
                    <div className="text-xs sm:text-sm text-white/70">{b.s}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
